import { ReactElement, createContext, useContext, useEffect, useState } from 'react';
import { noop } from '@synergy/utils';
import { useIsMobile } from '@synergy/components-next';
import { useNavigate, useParams } from 'react-router';

import { ArticleDetail as Article } from '../../models/article-detail';
import { ArticleDetailDesktop } from './article-detail-desktop';
import { ArticleDetailIdenticalProducts } from './article-detail-identical-products';
import { ArticleDetailMobile } from './article-detail-mobile';
import { ArticleVariant } from '../../models/article-variant';
import { AttributeOption, articleVariantsSelector } from '../../store/articles-variants-selector';
import { ROUTES } from '../../utils/routes';
import { addArticleToCart } from '../../store/shopping-cart-article-actions';
import { addToWishlist } from '../../store/whish-list-actions';
import { articleDetailSelector } from '../../store/articles-selectors';
import { articleDetailsReset, fetchArticleDetails } from '../../store/articles-actions';
import { openInquiryModal } from '../../store/single-inquiry-actions';
import { useAppDispatch, useAppSelector } from '../../../store/store';

export type ArticleDetailContextValue = {
    article: Article;
    orderQuantity: number;
    attributeOptions: AttributeOption[];
    variants: ArticleVariant[];
    onToggleFavorite: (id: number, quantity: number) => void;
    onAddToShoppingCart: (id: number, quantity: number) => void;
    onVariantSelected: (articleId: number) => void;
    onSingleInquiry: (quantity: number) => void;
    onChangeOrderQuantity: (quantity: number) => void;
};

const ArticleDetailContext = createContext<ArticleDetailContextValue>({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    article: undefined!,
    orderQuantity: 1,
    attributeOptions: [],
    variants: [],
    onToggleFavorite: noop,
    onAddToShoppingCart: noop,
    onVariantSelected: noop,
    onSingleInquiry: noop,
    onChangeOrderQuantity: noop,
});

export const useArticleDetailContext = (): ArticleDetailContextValue => useContext(ArticleDetailContext);

export const ArticleDetail = (): ReactElement | null => {
    const dispatch = useAppDispatch();
    const { id: articleIdParam } = useParams<{ id: string }>();
    const isMobile = useIsMobile();
    const article = useAppSelector(articleDetailSelector);
    const { variants, attributeOptions } = useAppSelector(articleVariantsSelector);
    const navigate = useNavigate();
    const [orderQuantity, setOrderQuantity] = useState<number>(1);

    useEffect(() => {
        setOrderQuantity(article?.minimumOrderQuantity ?? 1);
    }, [article]);

    useEffect(() => {
        if (articleIdParam && !isNaN(+articleIdParam)) {
            dispatch(fetchArticleDetails(+articleIdParam));
        }
        return () => {
            dispatch(articleDetailsReset());
        };
    }, [articleIdParam, dispatch]);

    if (!article) {
        return null;
    }

    const handleAddToShoppingCart = (id: number, quantity: number): void => {
        dispatch(addArticleToCart(id, quantity));
    };

    const handleVariantSelected = (articleId: number): void => {
        navigate(ROUTES.article.detail.replace(':id', articleId.toString()));
    };

    const handleToggleFavorite = (id: number, quantity: number): void => {
        dispatch(addToWishlist('default', id, quantity));
    };

    const handleSingleInquiry = (quantity: number): void => {
        dispatch(openInquiryModal({ items: [{ id: article.id, articleName: article.name, quantity }] }));
    };

    return (
        <ArticleDetailContext.Provider
            value={{
                article,
                orderQuantity: orderQuantity,
                attributeOptions,
                variants,
                onAddToShoppingCart: handleAddToShoppingCart,
                onToggleFavorite: handleToggleFavorite,
                onVariantSelected: handleVariantSelected,
                onSingleInquiry: handleSingleInquiry,
                onChangeOrderQuantity: setOrderQuantity,
            }}
        >
            <>
                {isMobile ? <ArticleDetailMobile /> : <ArticleDetailDesktop />}
                <ArticleDetailIdenticalProducts />
            </>
        </ArticleDetailContext.Provider>
    );
};
